// src/pages/AuthorityUAOverviewPage.js
// UNPUBLISHED (unlinked) authority utility-allowance landing page — the
// two-program split: one Section 8 / HCV locality schedule, and public
// housing allowances listed per development. Demo data for Waltham only;
// when this launches, the org/development lists move to config or the API.
//
// Route: /utility-allowances/:org/overview — not linked from anywhere.

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Brand from '../components/Brand';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const T = {
  blue600: '#2563eb',
  blue100: '#dbeafe',
  blue50: '#eff6ff',
  green600: '#059669',
  green100: '#d1fae5',
  green50: '#ecfdf5',
  gray900: '#111827',
  gray600: '#4b5563',
  gray500: '#5f6673', // darkened for 4.5:1 on light backgrounds (WCAG 1.4.3),
  gray400: '#6b7280', // darkened for 4.5:1 on white (WCAG 1.4.3),
  gray200: '#e5e7eb',
  gray100: '#f3f4f6',
  white: '#ffffff',
  radius: '16px',
  radiusSm: '12px',
  radiusPill: '999px',
  shadowSm: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
  gradient: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #faf5ff 100%)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const AUTHORITIES = {
  waltham: {
    name: 'Waltham Housing Authority',
    logo: '/authority-logos/waltham.png',
    hcv: {
      status: 'Coming soon',
      effective: 'Effective November 1, 2026',
      href: '/utility-allowances/waltham',
      clickable: true,
    },
    developments: [
      { slug: 'prospect-hill-terrace', name: 'Prospect Hill Terrace', detail: 'Family · 1-3 BR', status: 'Draft', clickable: true },
      { slug: 'dana-court', name: 'Dana Court', detail: 'Elderly/Disabled · 1 BR', status: 'Draft', clickable: true },
      { slug: 'chesterbrook-gardens', name: 'Chesterbrook Gardens', detail: 'Family · 2-3 BR', status: 'Draft', clickable: true },
    ],
  },
};

export default function AuthorityUAOverviewPage() {
  const mobile = useIsMobile();
  const { org } = useParams();
  const auth = AUTHORITIES[(org || '').toLowerCase()];

  const card = {
    background: T.white,
    border: `1px solid ${T.gray200}`,
    borderRadius: T.radius,
    boxShadow: T.shadowSm,
    padding: mobile ? '1.25rem 1rem' : '1.75rem',
  };
  const pill = (bg, fg) => ({
    fontSize: '0.72rem',
    padding: '0.18rem 0.6rem',
    background: bg,
    color: fg,
    borderRadius: T.radiusPill,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  });

  return (
    <div style={{ fontFamily: T.font, minHeight: '100vh', background: T.gradient, display: 'flex', flexDirection: 'column' }}>
      <header style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${T.gray200}`,
        padding: mobile ? '0.75rem 1rem' : '1.25rem 1.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', maxWidth: '1400px', margin: '0 auto', flexWrap: 'wrap' }}>
          <Brand as="a" href="https://gethousingai.com" theme="light" markClassName="text-blue-600"
            style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '1.1rem' : '1.35rem', textDecoration: 'none' }} />
          <span style={{ color: '#d1d5db', fontSize: '1.5rem', fontWeight: 300 }}>|</span>
          <span style={{ fontWeight: 700, color: T.gray900, fontSize: mobile ? '0.95rem' : '1.1rem' }}>
            {auth ? auth.name : 'Housing Authority'}
          </span>
          <span style={pill(T.blue100, T.blue600)}>Utility Allowances</span>
        </div>
      </header>

      <main style={{ padding: mobile ? '1.5rem 0.75rem' : '3rem 1.5rem', flex: 1 }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          {!auth ? (
            <div style={{ ...card, textAlign: 'center', padding: '3rem' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: T.gray900, margin: '0 0 0.5rem' }}>Unknown housing authority</h3>
              <p style={{ color: T.gray500, fontSize: '0.9rem', margin: 0 }}>No utility allowance overview for &ldquo;{org}&rdquo;.</p>
            </div>
          ) : (
            <>
              <h1 style={{ fontSize: mobile ? '1.25rem' : '1.5rem', fontWeight: 800, color: T.gray900, margin: '0 0 0.35rem', letterSpacing: '-0.02em' }}>
                Utility Allowances
              </h1>
              <p style={{ color: T.gray600, fontSize: '0.9rem', margin: '0 0 1.75rem', lineHeight: 1.6 }}>
                {auth.name} maintains utility allowances under two programs. Pick yours below.
              </p>

              {/* ── Section 8 / HCV ── */}
              <div style={{ ...card, borderTop: `3px solid ${T.blue600}`, marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                  <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: T.gray900, margin: 0 }}>
                    Section 8 / Housing Choice Voucher
                  </h2>
                  <span style={pill(T.blue100, T.blue600)}>24 CFR 982.517</span>
                </div>
                <p style={{ color: T.gray500, fontSize: '0.85rem', margin: '0 0 1rem', lineHeight: 1.6 }}>
                  One schedule for the whole locality — used when your voucher rents a private
                  apartment. Allowances vary by unit type, bedroom size, and who pays each utility.
                </p>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap',
                  background: T.blue50, border: `1px solid ${T.blue100}`, borderRadius: T.radiusSm, padding: '0.85rem 1rem',
                }}>
                  <div>
                    <div style={{ fontWeight: 700, color: T.gray900, fontSize: '0.92rem' }}>Utility Allowance Schedule</div>
                    <div style={{ color: T.gray500, fontSize: '0.78rem' }}>{auth.hcv.effective}</div>
                  </div>
                  {auth.hcv.clickable ? (
                    <Link to={auth.hcv.href} style={{ ...pill(T.blue600, T.white), textDecoration: 'none', padding: '0.45rem 1rem', fontSize: '0.82rem' }}>
                      View schedule
                    </Link>
                  ) : (
                    <span style={pill(T.gray100, T.gray500)}>{auth.hcv.status}</span>
                  )}
                </div>
              </div>

              {/* ── Public Housing ── */}
              <div style={{ ...card, borderTop: `3px solid ${T.green600}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                  <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: T.gray900, margin: 0 }}>
                    Public Housing
                  </h2>
                  <span style={pill(T.green100, T.green600)}>24 CFR 965 / 760 CMR</span>
                </div>
                <p style={{ color: T.gray500, fontSize: '0.85rem', margin: '0 0 1rem', lineHeight: 1.6 }}>
                  Allowances are set separately for each development the authority owns, covering
                  only the utilities tenants pay directly. Find your development below.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {auth.developments.map((d) => (
                    <div key={d.slug} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap',
                      background: T.green50, border: `1px solid ${T.green100}`, borderRadius: T.radiusSm, padding: '0.85rem 1rem',
                    }}>
                      <div>
                        <div style={{ fontWeight: 700, color: T.gray900, fontSize: '0.92rem' }}>{d.name}</div>
                        <div style={{ color: T.gray500, fontSize: '0.78rem' }}>{d.detail}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={pill(T.gray100, T.gray500)}>{d.status}</span>
                        {d.clickable && (
                          <Link to={`/utility-allowances/public-housing/${d.slug}`} style={{ ...pill(T.green600, T.white), textDecoration: 'none', padding: '0.45rem 1rem', fontSize: '0.82rem' }}>
                            View allowances
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <footer style={{ background: T.gray900, color: T.white, padding: mobile ? '1.25rem 0' : '2rem 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <Brand theme="dark" markClassName="text-blue-600"
            style={{ fontWeight: 600, fontSize: '0.95rem' }} />
          <div style={{ fontSize: '0.85rem', color: '#d1d5db' }}>© 2026 HousingAI. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
